import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

interface SupportRequest {
  name: string;
  email: string;
  issueType: string;
  message: string;
  imageUrl?: string;
}

interface SupportState {
  isOpen: boolean;
  isSubmitting: boolean;
  error: string | null;
  lastSubmissionId: string | null;
}

// Get API keys from environment variables
const SLACK_BOT_TOKEN = import.meta.env.VITE_SLACK_BOT_TOKEN || '';
const SLACK_CHANNEL_ID = import.meta.env.VITE_SLACK_CHANNEL_ID || 'C09B8G4CCJD';
const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'dayxmgiau';
const CLOUDINARY_UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || 'feedback_uploads';

// Async thunk for uploading image to Cloudinary
export const uploadImageToCloudinary = createAsyncThunk(
  'support/uploadImage',
  async (file: File, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to upload image');
      }

      const data = await response.json();
      return data.secure_url;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to upload image');
    }
  }
);

// Async thunk for sending support request to Slack
export const sendSupportRequest = createAsyncThunk(
  'support/sendRequest',
  async (request: SupportRequest, { rejectWithValue }) => {
    try {
      const slackMessage = {
        text: 'New Support Request',
        blocks: [
          {
            type: 'header',
            text: {
              type: 'plain_text',
              text: '🆘 New Support Request',
            },
          },
          {
            type: 'section',
            fields: [
              {
                type: 'mrkdwn',
                text: `*Name:*\n${request.name}`,
              },
              {
                type: 'mrkdwn',
                text: `*Email:*\n${request.email}`,
              },
              {
                type: 'mrkdwn',
                text: `*Issue Type:*\n${request.issueType}`,
              },
              {
                type: 'mrkdwn',
                text: `*Timestamp:*\n${new Date().toLocaleString()}`,
              },
            ],
          },
          {
            type: 'section',
            text: {
              type: 'mrkdwn',
              text: `*Message:*\n${request.message || 'No message provided'}`,
            },
          },
          ...(request.imageUrl
            ? [
                {
                  type: 'image',
                  image_url: request.imageUrl,
                  alt_text: 'Support attachment',
                },
              ]
            : []),
        ],
      };

      const response = await fetch('https://slack.com/api/chat.postMessage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SLACK_BOT_TOKEN}`,
        },
        body: JSON.stringify({
          channel: SLACK_CHANNEL_ID,
          ...slackMessage,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message to Slack');
      }

      const data = await response.json();
      if (!data.ok) {
        throw new Error(data.error || 'Failed to send message to Slack');
      }

      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send support request');
    }
  }
);

const initialState: SupportState = {
  isOpen: false,
  isSubmitting: false,
  error: null,
  lastSubmissionId: null,
};

const supportSlice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    openSupport: (state) => {
      state.isOpen = true;
      state.error = null;
    },
    closeSupport: (state) => {
      state.isOpen = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Upload image cases
    builder
      .addCase(uploadImageToCloudinary.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(uploadImageToCloudinary.fulfilled, (state) => {
        // Keep submitting state true because we'll send to Slack next
        state.error = null;
      })
      .addCase(uploadImageToCloudinary.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      });

    // Send support request cases
    builder
      .addCase(sendSupportRequest.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(sendSupportRequest.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.isOpen = false;
        state.error = null;
        state.lastSubmissionId = action.meta.requestId;
      })
      .addCase(sendSupportRequest.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      });
  },
});

export const { openSupport, closeSupport, clearError } = supportSlice.actions;
export default supportSlice.reducer;
